import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import { qk } from './queries';
import type { ApiEnvelope } from './types';

export interface ProductPayload {
  name: string;
  category: string | null;
  unitOfMeasure: string;
  primarySupplierId: string;
  primarySku: string;
  fallbacks: Array<{ supplierId: string; sku: string }>;
}

export function useSubmitProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductPayload) =>
      apiFetch<ApiEnvelope<{ id: string }>>('/api/admin/products', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminProducts }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: ProductPayload & { id: string }) =>
      apiFetch<ApiEnvelope<{ id: string }>>(`/api/admin/products/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminProducts }),
  });
}

export function useDeactivateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<ApiEnvelope<{ id: string }>>(`/api/admin/products/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminProducts }),
  });
}

export interface CreateUserPayload {
  email: string;
  name?: string;
  role: 'chef' | 'super_admin';
  locationIds: string[];
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateUserPayload) =>
      apiFetch<ApiEnvelope<{ id: string }>>('/api/admin/users', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; name?: string; role?: string; locationIds?: string[]; isActive?: boolean }) =>
      apiFetch<ApiEnvelope<{ id: string }>>(`/api/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<ApiEnvelope<{ id: string }>>(`/api/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; address?: string }) =>
      apiFetch<ApiEnvelope<{ id: string }>>('/api/admin/locations', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminLocations }),
  });
}

export { safeMessage } from './queries';
